import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { Stack } from '@mui/system'
import React, { Dispatch, FC, SetStateAction, useMemo } from 'react'
import {
    customBorderRadius,
    errorColor,
    theme,
} from '../../lib/styles/universal'
import { colors, OrganizationWithMembers } from '@hazadapt-git/public-core-base'
import { IoOpenOutline } from 'react-icons/io5'
import { useWindowSizeUp } from '../../lib/utils'
import {
    SimplifiedSeatSubscription,
    useBilling,
} from '../../lib/hooks/useBilling'
import { makeStyles } from 'tss-react/mui'
import { SeatSubscriptionTable } from '../organisms/SeatSubscriptionTable'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'

interface ManageSubscriptionsTemplateProps {
    page: 'manage' | 'cancel' | 'success'
    fullOrganization: OrganizationWithMembers
    onNavigateToCancelSeats(sub: SimplifiedSeatSubscription): void
    onRestoreSeatsSubscription(sub: SimplifiedSeatSubscription): void
    checked: boolean
    setChecked: Dispatch<SetStateAction<boolean>>
    subscriptionType: 'core' | 'seat'
    selectedSubscription?: SimplifiedSeatSubscription
}

export const ManageSubscriptionsTemplate: FC<
    ManageSubscriptionsTemplateProps
> = ({
    page,
    fullOrganization,
    onNavigateToCancelSeats,
    onRestoreSeatsSubscription,
    checked,
    setChecked,
    subscriptionType,
    selectedSubscription,
}) => {
    const { classes: localClasses } = useLocalStyles()
    const mdScreen = useWindowSizeUp('md')
    const { rootSubscriptionHumanReadableEndDate } = useBilling()

    const seatsReferenceText = useMemo(() => {
        if (subscriptionType === 'core' || !selectedSubscription) return null
        return selectedSubscription.seatCount === 1
            ? 'this seat'
            : 'these seats'
    }, [subscriptionType, selectedSubscription])

    if (page === 'manage') {
        return (
            <>
                {/* Current seat tally */}
                {fullOrganization && fullOrganization.seats > 0 ? (
                    <Typography
                        className={localClasses.mdBoldText}
                        component={'span'}
                    >
                        You are currently using{' '}
                        <Typography
                            fontSize={'inherit'}
                            fontWeight={700}
                            component={'span'}
                        >
                            {fullOrganization.members.length} out of{' '}
                            {fullOrganization.seats}
                        </Typography>{' '}
                        seats
                    </Typography>
                ) : (
                    <Typography
                        className={localClasses.mdBoldText}
                        component={'span'}
                    >
                        You currently have no seats
                    </Typography>
                )}
                <SeatSubscriptionTable
                    onNavigateToCancelSeats={onNavigateToCancelSeats}
                    onRestoreSubscription={onRestoreSeatsSubscription}
                />
            </>
        )
    }

    if (page === 'cancel') {
        return (
            <Stack gap={'2rem'} maxWidth={'55rem'}>
                {subscriptionType === 'core' ? (
                    <div>
                        <Typography className={localClasses.mdBoldText}>
                            We're sorry to hear that you'd like to cancel your
                            ResiliencePoint subscription.
                        </Typography>
                        <Typography
                            className={localClasses.mdBoldText}
                            color={errorColor}
                            fontWeight={500}
                        >
                            You can keep using ResiliencePoint until{' '}
                            {rootSubscriptionHumanReadableEndDate}.
                        </Typography>
                    </div>
                ) : selectedSubscription ? (
                    <Typography
                        className={localClasses.mdBoldText}
                        color={errorColor}
                    >
                        You can keep using {seatsReferenceText} until{' '}
                        {selectedSubscription.cancelDate ??
                            selectedSubscription.renewalDate}
                        .
                    </Typography>
                ) : null}
                {subscriptionType === 'seat' && selectedSubscription && (
                    <Typography
                        variant={mdScreen ? 'body1' : 'body2'}
                        fontWeight={500}
                    >
                        When this subscription ends, {seatsReferenceText} will
                        no longer be available to your organization, and you
                        will no longer be charged for{' '}
                        {selectedSubscription.seatCount === 1 ? 'it' : 'them'}.
                    </Typography>
                )}
                {/* Contact us */}
                <Stack gap={'1rem'} className={localClasses.contactUsContainer}>
                    <Typography className={localClasses.mdBoldText}>
                        Have any question or issues?
                    </Typography>
                    <Typography variant={mdScreen ? 'body1' : 'body2'}>
                        Feel free to reach out to our support with questions or
                        concerns.
                    </Typography>
                    <Button
                        sx={{ alignSelf: 'end' }}
                        href="https://www.hazadapt.com/contact"
                        target="_blank"
                        variant="outlined"
                    >
                        Contact Us{' '}
                        <IoOpenOutline style={{ marginLeft: '0.5rem' }} />
                    </Button>
                </Stack>
                {/* Checkbox */}
                <FormControlLabel
                    sx={{ alignItems: 'flex-start' }}
                    control={
                        <Checkbox
                            checked={checked}
                            onChange={() => setChecked(!checked)}
                        />
                    }
                    label={
                        <>
                            <Typography
                                variant={mdScreen ? 'body1' : 'body2'}
                                fontWeight={500}
                            >
                                I have read and understand that{' '}
                                {subscriptionType === 'seat'
                                    ? `${
                                          selectedSubscription?.cancelDate
                                              ? `starting ${selectedSubscription.cancelDate}`
                                              : 'after the last day of this subscription'
                                      }, ${seatsReferenceText} will no longer be available to my organization.`
                                    : `starting ${rootSubscriptionHumanReadableEndDate}, my organization will no longer have access to ResiliencePoint.`}
                            </Typography>
                            <Typography variant={mdScreen ? 'body1' : 'body2'}>
                                {subscriptionType === 'seat'
                                    ? `I have removed team members from ${seatsReferenceText}, or I plan to do so before ${
                                          selectedSubscription
                                              ? selectedSubscription.cancelDate
                                              : 'the subscription ends'
                                      }.`
                                    : `I acknowledge that on ${rootSubscriptionHumanReadableEndDate}, all associated local safety information, including hazard guides, will be removed from HazAdapt.`}
                            </Typography>
                        </>
                    }
                />
            </Stack>
        )
    }

    if (page === 'success') {
        return (
            <>
                <Alert severity="success" sx={{ width: 'fit-content' }}>
                    <AlertTitle>Cancellation Successful</AlertTitle>A
                    confirmation email has been sent to all organization
                    administrators.
                </Alert>
                <Typography
                    variant={mdScreen ? 'body1' : 'body2'}
                    fontWeight={500}
                >
                    {subscriptionType === 'core'
                        ? "We're sorry to see you go! "
                        : null}
                    You can keep using{' '}
                    {subscriptionType === 'core'
                        ? 'ResilencePoint'
                        : seatsReferenceText}{' '}
                    until{' '}
                    {subscriptionType === 'core'
                        ? rootSubscriptionHumanReadableEndDate
                        : selectedSubscription?.cancelDate ??
                          selectedSubscription?.renewalDate}
                    .
                </Typography>
                {subscriptionType === 'core' && (
                    <Typography variant="body2">
                        <strong>Please note:</strong> On{' '}
                        {rootSubscriptionHumanReadableEndDate}, all associated
                        local safety information, including hazard guides, will
                        be removed from HazAdapt.
                    </Typography>
                )}
            </>
        )
    }

    return null
}

const useLocalStyles = makeStyles()({
    mdBoldText: {
        fontSize: '1rem',
        fontWeight: 500,
        [theme.breakpoints.up('md')]: { fontSize: '1.25rem' },
    },
    contactUsContainer: {
        backgroundColor: colors.grays.BLANC,
        borderRadius: customBorderRadius,
        boxShadow: theme.shadows[4],
        padding: '1rem .5rem 1rem 1.5rem',
        maxWidth: '20rem',
    },
})
